import { createBottomTabNavigator, type BottomTabBarProps } from '@react-navigation/bottom-tabs';
import type { NavigatorScreenParams } from '@react-navigation/native';
import { useIsFocused } from '@react-navigation/native';
import { isLoggedInAtom, type AuthenticationGateSource } from 'app/atoms';
import { NavigationBar } from 'app/components/navigation-bar';
import { TabSceneTransition } from 'app/components/tab-scene-transition';
import { useFacebookSDK } from 'app/hooks/analytics';
import { useSetupNotificationsBackgroundTask } from 'app/hooks/use-background-notifications-task';
import * as billboard from 'app/screens/billboard/navigator';
import * as chat from 'app/screens/chat/navigator';
import * as management from 'app/screens/management/navigator';
import * as notifications from 'app/screens/notifications/navigator';
import * as profile from 'app/screens/profile/navigator';
import { useAtomValue } from 'jotai';
import { useEffect, type ReactNode } from 'react';
import { useIsGuestWeb } from 'app/utils/web-helpers';
import { useAuthorize } from 'app/hooks/use-authorize';
import { navigationRef } from 'app/lib/root-navigation';
import { ListingType } from 'app/types/listing';

export type Parameters = {
  billboard: NavigatorScreenParams<billboard.Parameters> | undefined;
  chat: NavigatorScreenParams<chat.Parameters> | undefined;
  management: NavigatorScreenParams<management.Parameters> | undefined;
  notifications: NavigatorScreenParams<notifications.Parameters> | undefined;
  profile: NavigatorScreenParams<profile.Parameters> | undefined;
};

export { linking } from './main-tab.linking';

const Tab = createBottomTabNavigator<Parameters>();

function BillboardTab() {
  return (
    <TabSceneTransition>
      <billboard.Navigator />
    </TabSceneTransition>
  );
}

function GuestProtectedTab({ children, source }: { children: ReactNode; source: AuthenticationGateSource }) {
  const isGuestWeb = useIsGuestWeb();
  const authorize = useAuthorize(source);

  useEffect(() => {
    if (!isGuestWeb) return;
    if (navigationRef.isReady()) {
      navigationRef.navigate(
        'main',
        {
          screen: 'billboard',
          params: { screen: 'all-listings', params: { activeTab: ListingType.offer, type: ListingType.offer } },
        },
        { pop: true },
      );
    }
    authorize(() => undefined);
  }, [authorize, isGuestWeb]);

  return isGuestWeb ? null : children;
}

function ManagementTab() {
  const focused = useIsFocused();
  if (!focused) return null;
  return (
    <GuestProtectedTab source="contact">
      <TabSceneTransition>
        <management.Navigator />
      </TabSceneTransition>
    </GuestProtectedTab>
  );
}

function ChatTab() {
  return (
    <GuestProtectedTab source="contact">
      <TabSceneTransition>
        <chat.Navigator />
      </TabSceneTransition>
    </GuestProtectedTab>
  );
}

function NotificationsTab() {
  return (
    <GuestProtectedTab source="header">
      <TabSceneTransition>
        <notifications.Navigator />
      </TabSceneTransition>
    </GuestProtectedTab>
  );
}

function ProfileTab() {
  return (
    <GuestProtectedTab source="member-profile">
      <TabSceneTransition>
        <profile.Navigator />
      </TabSceneTransition>
    </GuestProtectedTab>
  );
}

export function Navigator() {
  useFacebookSDK();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isGuestWeb = useIsGuestWeb();
  useSetupNotificationsBackgroundTask();
  const tabBar = (props: BottomTabBarProps) => {
    const currentTabRoute = props.state.routes[props.state.index];
    const nestedState = currentTabRoute?.state;
    const currentNestedRoute = nestedState?.routes[nestedState.index ?? 0];

    if (isLoggedIn && currentNestedRoute?.name === 'all-listings') {
      return null;
    }

    return <NavigationBar {...props} />;
  };

  return (
    <Tab.Navigator
      id="main-tab"
      screenOptions={{ headerShown: false }}
      tabBar={isLoggedIn || isGuestWeb ? tabBar : () => null}
    >
      <Tab.Screen component={BillboardTab} name="billboard" />
      <Tab.Screen component={ManagementTab} name="management" />
      <Tab.Screen component={ChatTab} name="chat" />
      <Tab.Screen component={NotificationsTab} name="notifications" />
      <Tab.Screen component={ProfileTab} name="profile" />
    </Tab.Navigator>
  );
}
